import { useQuery } from '@tanstack/react-query';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { tokensService } from '@/features/payment/api/tokens';

/**
 * Token balance is owned by TanStack Query (see STATE_POLICY.md).
 *
 * Authority order:
 *   1. Auth flows (`profileService.applyProfileData`) seed via `setQueryData`
 *      on login/register/refresh — populates the cache before any UI mounts.
 *   2. WS handler `users.ts` pushes deltas via `setQueryData` when the server
 *      broadcasts a new balance.
 *   3. This hook's `queryFn` only runs as a cold-start fallback — if a tab
 *      mounts before any seeder has fired.
 *
 * `staleTime: Infinity` is deliberate — WS is the live-update channel, so a
 * background refetch on focus/mount would just race the WS payload.
 */
function useTokenBalanceQuery() {
  return useQuery({
    queryKey: cacheKeys.balance,
    queryFn: async () => {
      const response = await tokensService.getBalance();
      return response.data.attributes.current_balance;
    },
    staleTime: Infinity,
  });
}

export function useTokenBalance(): number {
  const { data } = useTokenBalanceQuery();
  return data ?? 0;
}

/**
 * Raw balance, or `undefined` until the first seeder fires. The nav balance
 * pill uses this so it can hide itself before any value loads rather than
 * flashing "0" — `useTokenBalance()` returns `data ?? 0` which papers over the
 * uninitialised case. The display value is always clamped to ≥0: the ledger
 * may go negative internally (root CLAUDE.md "Token balance"), but we never
 * show a debt balance to the user, so `formatTokenBalance` floors negatives to
 * "0" (2026-05-27, reversing the 2026-05-24 "show -22" decision).
 */
export function useRawTokenBalance(): number | undefined {
  const { data } = useTokenBalanceQuery();
  return data;
}

export function useTokenBalanceInitialized(): boolean {
  const { data } = useTokenBalanceQuery();
  return data !== undefined;
}
